<template>
  <v-form ref="form">
    <FormPanels>
      <FormPanel label="Video Info">
        <FormRow
          required
          label="Name"
          tooltip="An internal label to help you identify this video. Not visible to guests."
        >
          <TextField
            :rules="[validation.required]"
            placeholder="e.g. Keynote Recap"
            v-model="form.title"
          ></TextField>
        </FormRow>

        <FormRow label="Description">
          <RichTextEditor v-model="form.description" simple />
        </FormRow>

        <FormRow required label="Source">
          <v-btn-toggle v-model="form.source_type" mandatory dense>
            <v-btn value="vimeo">Vimeo</v-btn>
            <v-btn value="upload">Upload MP4</v-btn>
          </v-btn-toggle>
        </FormRow>

        <FormRow
          v-if="form.source_type == 'vimeo'"
          required
          label="Vimeo Video"
        >
          <VimeoVideoField v-model="vimeoValue" :video-id="form.id" />
        </FormRow>

        <FormRow v-else required label="Video File">
          <div v-if="currentFileUrl" class="mb-2">
            <video
              :src="currentFileUrl"
              controls
              style="max-width: 400px; width: 100%; border-radius: 8px"
            ></video>
          </div>
          <v-file-input
            v-model="form.file"
            accept="video/*"
            :label="currentFileUrl ? 'Replace video file' : 'Choose video file'"
            outlined
            dense
            hide-details
          ></v-file-input>
        </FormRow>
      </FormPanel>

      <FormPanel label="Call to Action Buttons">
        <CtaList v-model="ctas" :max="3" />
      </FormPanel>

      <FormPanel label="Video Details">
        <FormRow
          required
          label="URL"
          tooltip="The path where this video's page will live on your Commons domain."
        >
          <v-layout align-center>
            <span class="mr-3">
              <div v-if="currentUser.active_project_env == 'production'">
                {{ productionDomain }}/watch/
              </div>
              <div v-else>{{ stagingDomain }}/watch/</div>
            </span>
            <div class="flex-grow-1">
              <TextField
                ref="slugField"
                :rules="[validation.required, slugRule, slugAvailableRule]"
                placeholder="my-video"
                v-model="form.slug"
              />
              <div v-if="slugCheck.loading" class="slug-check-hint">
                Checking availability…
              </div>
            </div>
          </v-layout>
        </FormRow>
      </FormPanel>
    </FormPanels>

    <SubmitFooter :refs="$refs" :submit="submit" :disabled="slugCheck.loading"
      >{{ btnText }}
      <template v-if="form.id" #actions>
        <div>
          <v-btn
            @click="$emit('destroy')"
            outlined
            color="primary"
            elevation="0"
            rounded
            large
          >
            Delete
          </v-btn>
        </div>
      </template>
    </SubmitFooter>
  </v-form>
</template>

<script>
import debounce from "lodash/debounce";
import mixin from "../_extends/form.extends.js";
import VimeoVideoField from "@/components/video/VimeoVideoField.vue";
import CtaList from "@/components/video/CtaList.vue";

export default {
  extends: mixin,
  components: { VimeoVideoField, CtaList },
  data() {
    return {
      slugRule: (value) =>
        !value ||
        /^[a-zA-Z0-9-]+$/.test(value) ||
        "Only letters, numbers, and dashes are allowed",
      // Tracks the most recently *checked* slug so the field can tell a
      // stale result (for a value the user has since changed) from a live
      // one — see watch: { "form.slug" }.
      slugCheck: {
        slug: null,
        taken: false,
        loading: false,
      },
    };
  },
  watch: {
    // Editing an existing video's URL, then changing your mind and typing
    // the original slug back in, "just works": the check excludes the
    // video's own watch page (via page_ids), so re-checking the original
    // value finds no *other* page using it and clears the error.
    "form.slug"(value, oldValue) {
      if (value === oldValue) return;

      if (!value || !this.slugRule(value)) {
        this.slugCheck = { slug: value, taken: false, loading: false };
        this.debouncedCheckSlug.cancel();
        return;
      }

      this.slugCheck.loading = true;
      this.debouncedCheckSlug();
    },
  },
  created() {
    this.debouncedCheckSlug = debounce(this.checkSlugAvailability, 500);
  },
  beforeDestroy() {
    this.debouncedCheckSlug.cancel();
  },
  computed: {
    stagingDomain() {
      if (this.$isProjectSlug("ideasunited.com")) {
        return "https://staging.ideasunited.com";
      }
      return `https://${this.currentUser?.staging_domain}`;
    },
    productionDomain() {
      if (this.$isProjectSlug("ideasunited.com")) {
        return "https://new.ideasunited.com";
      }
      return `https://${this.currentUser?.production_domain}`;
    },
    currentFileUrl() {
      const file = this.form.file;
      if (!file) return null;
      if (typeof file === "string") return file;
      return file.url || null;
    },
    vimeoValue: {
      get() {
        if (!this.form.vimeo_id) return null;
        return {
          vimeo_id: this.form.vimeo_id,
          vimeo_h: this.form.vimeo_h,
          ...(this.form.vimeo_data || {}),
        };
      },
      set(val) {
        if (!val) {
          this.form.vimeo_id = null;
          this.form.vimeo_h = null;
          this.form.vimeo_url = "";
          this.form.vimeo_data = {};
          return;
        }
        const { vimeo_id, vimeo_h, ...rest } = val;
        this.form.vimeo_id = vimeo_id;
        this.form.vimeo_h = vimeo_h;
        this.form.vimeo_url = rest.link || this.form.vimeo_url;
        this.form.vimeo_data = rest;
      },
    },
    ctas: {
      get() {
        return (this.form.data && this.form.data.ctas) || [];
      },
      set(val) {
        this.form.data = { ...(this.form.data || {}), ctas: val };
      },
    },
  },
  methods: {
    // Synchronous rule so v-form's normal validate() (run on submit, see
    // SubmitFooter) picks it up — only errors when it's reporting on the
    // exact value currently in the field, so a result for a slug the user
    // has since edited away from never blocks submission.
    slugAvailableRule(value) {
      if (!this.slugCheck.taken || this.slugCheck.slug !== value) return true;
      return "This URL is already in use — choose another.";
    },
    async checkSlugAvailability() {
      const slug = this.form.slug;
      if (!slug) return;

      try {
        const { data } = await this.$api.Page.checkUrl({
          url: `watch/${slug}`,
          // Exclude this video's own watch page so re-typing the current
          // (or original, when editing) slug never flags itself as taken.
          id: this.form.page_ids?.[0],
          // Videos are always staged regardless of the admin's active
          // environment toggle (see VideosController#before_all) — the
          // watch page this slug would collide with lives there too.
          context: "staging",
        });

        // The slug may have changed again while this request was in
        // flight; only apply the result if it's still current.
        if (this.form.slug !== slug) return;

        this.slugCheck = { slug, taken: data.exists, loading: false };
        this.$nextTick(() => this.$refs.slugField?.validate());
      } catch (e) {
        if (this.form.slug === slug) this.slugCheck.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.slug-check-hint {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
  margin-top: 4px;
}
</style>
